<template>
  <div class="container mx-auto px-4" style="max-width: 1200px;">
    <div class="text-center mt-8">
      <h1
        class="
          text-2xl tracking-tight font-black text-gray-900 sm:text-3xl
        "
      >
        <span>Choose Your - </span>
        <span class="text-mygrad">Graduation Keepsake</span>
      </h1>
      <p
        class="
          mt-3 max-w-md mx-auto text-sm text-gray-700 sm:text-base md:mt-5 md:max-w-lg
        "
      >
        Celebrate your special day with our exclusive Graduation products. Choose from a digital download, a personalised clip<span v-if="showBundle">, or a bundle</span>.
      </p>
      <button v-if="this.$route.params.uni == 'nottingham'"
        type="button"
        @click="goToLink"
        class="
          inline-flex
          mt-3
          items-center
          px-4
          py-2
          border border-transparent
          shadow-sm
          text-lg
          font-medium
          rounded-md
          text-white
          bg-mygrad
          hover:bg-mygrad-dark
          focus:outline-none
          focus:ring-2
          focus:ring-offset-2
          focus:ring-mygrad
        "
      >
        For ceremonies before July 2024
      </button>
    </div>
    <div :class="mobileGridClass">
      <div
        v-for="product in products"
        :key="product.key"
        class="flex flex-col items-center border p-4 rounded-lg shadow-md pb-8 bg-white"
      >
        <img :src="product.image" @click="product.action" :alt="product.alt" class="w-full h-64 object-cover mb-4 rounded-lg">
        <p class="mt-0 text-lg font-bold text-gray-700">Price: {{ formatPrice(product.price) }}</p>
      </div>
    </div>
    <div :class="desktopGridClass">

      <div
        v-for="product in products"
        :key="product.key"
        class="flex flex-col items-center border p-4 rounded-lg shadow-md pb-8 bg-white"
      >
        <img :src="product.image" @click="product.action" :alt="product.alt" class="w-full h-64 object-cover mb-4 rounded-lg">
        <h2 class="text-xl font-bold text-gray-900 text-center">{{ product.title }}</h2>
        <p class="mt-2 text-base text-gray-700 sm:text-lg text-center">
          {{ product.description }}
        </p>
        <p class="mt-2 text-lg font-bold text-gray-700">Price: {{ formatPrice(product.price) }}</p>
        <button
          type="submit"
          @click="product.action"
          class="
            inline-flex mt-3 items-center justify-center px-4 py-2 border border-blue-300 shadow-sm text-lg font-medium rounded-md w-full text-white bg-blue-700 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
          "
        >
          Buy now
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_PRICES = {
  clip: { price: 2000, currency: "gbp" },
  digital_download: { price: 3000, currency: "gbp" },
  bundle: { price: 4000, currency: "gbp" },
};

const PRICE_OVERRIDES = {
  nottingham: {
    bundle: { price: 3500, currency: "gbp" },
  },
  queensland: {
    clip: { price: 5500, currency: "aud" },
    digital_download: { price: 6500, currency: "aud" },
    bundle: null,
  },
   uwa: {
    clip: { price: 4500, currency: "aud" },
    digital_download: { price: 5000, currency: "aud" },
    bundle: null,
  },
};

export default {
  name: "NottinghamListingPage",
  props: {
    title: String,
    organiserName: String,
    organiserSlug: String,
    organiser: Object,
  },
  data: function () {
    return {
      form: {
        name: "",
      },
      prices: this.defaultPrices(),
    };
  },
  computed: {
    showBundle() {
      return Boolean(this.prices.bundle && this.prices.bundle.price);
    },
    products() {
      const products = [
        {
          key: "clip",
          title: "Personalised Graduation Clip",
          description: "Receive a personalised GraduationClip of your graduation ceremony, highlighting only your special moment.",
          image: require("@/assets/personalised-graduation-clip.jpg"),
          alt: "Personalised Clip Image",
          price: this.prices.clip,
          action: this.clip,
        },
        {
          key: "digital_download",
          title: "Digital Download",
          description: "A Digital Download provides full coverage of your graduation ceremony, capturing all graduates, speeches and your moment as you cross the centre stage.",
          image: require("@/assets/digital-film-download-colour.jpg"),
          alt: "Digital Download Image",
          price: this.prices.digital_download,
          action: this.digitalDownload,
        },
      ];
      if (this.showBundle) {
        products.push({
          key: "bundle",
          title: "Graduation Bundle",
          description: "Enjoy the best of both worlds with our Graduation Bundle. Includes both the digital download and the personalised clip.",
          image: require("@/assets/personalised-graduation-clip-and-download.jpg"),
          alt: "Bundle Image",
          price: this.prices.bundle,
          action: this.bundle,
        });
      }
      return products;
    },
    mobileGridClass() {
      return [
        "grid md:hidden gap-2 mt-8",
        this.showBundle ? "grid-cols-3" : "grid-cols-2",
      ];
    },
    desktopGridClass() {
      return [
        "grid grid-cols-1 gap-8 mt-8",
        this.showBundle ? "md:grid-cols-3" : "md:grid-cols-2",
      ];
    },
  },
  methods: {
    submit() {
      this.$emit("submit", this.form);
    },
    defaultPrices() {
      return {
        ...DEFAULT_PRICES,
        ...(PRICE_OVERRIDES[this.$route.params.uni] || {}),
      };
    },
    formatPrice(price) {
      if (!price || !price.price) return "Unavailable";
      const amount = Number(price.price) / 100;
      const formattedAmount = Number.isInteger(amount)
        ? String(amount)
        : amount.toFixed(2);
      if (String(price.currency).toLowerCase() === "aud") {
        return `AUD$${formattedAmount}`;
      }
      return `£${formattedAmount}`;
    },
    digitalDownload() {
      this.$router.push({path: `/store/${this.$route.params.uni}/purchase/digital-download`})
    },
    clip() {
      this.$router.push({path: `/store/${this.$route.params.uni}/purchase`})
    },
    bundle() {
      this.$router.push({path: `/store/${this.$route.params.uni}/purchase?bundle=True`})
    },
    goToLink() {
      window.location.href = "https://shop2.mygraduationfilm.com/collections/nottingham-university"
    }
  },
  
};
</script>
